/**
 * Profile API Client
 *
 * API Endpoints:
 * - GET /profile → User
 * - POST /profile/update-profile → User
 * - POST /profile/update-dp → User
 * - POST /profile/update-email → User
 * - POST /profile/update-contact → User
 * - POST /profile/update-password → { message: string }
 * - POST /profile/subtitle-language → { message: string } (flat — no User envelope)
 * - POST /profile/contract/sign → User
 * - POST /me/location → { message, location } (flat — no User envelope)
 */

import type { ApiResponse, User, UserLocation } from '../types';
import { apiClient } from './base-client';

export const SUBTITLE_LANGUAGE_MAX_LEN = 10;

export interface UpdateProfileData {
  first_name?: string;
  last_name?: string;
  display_name?: string;
  handler?: string;
  gender?: string;
  country_id?: number;
  phone_number?: string;
}

export interface UpdateEmailData {
  email: string;
  password: string;
}

export interface UpdateContactData {
  phone?: string;
}

export interface UpdatePasswordData {
  current_password: string;
  password: string;
  password_confirmation: string;
}

/**
 * `POST /me/location` body. Two valid shapes (BE `UpdateUserLocation`):
 *   - Shape A (opt-in): `{ latitude, longitude, share_location: true }`
 *   - Shape B (opt-out): `{ share_location: false }` — no coords; clears stored coords
 * Coords are optional so the settings toggle can opt out without re-requesting geolocation.
 */
export interface ShareLocationData {
  latitude?: number;
  longitude?: number;
  share_location: boolean;
}

export const profileClient = {
  /**
   * Get user profile
   */
  get: async (): Promise<User> => {
    const data = await apiClient.get<ApiResponse<User>>('/profile');
    return data.data;
  },

  /**
   * Update profile
   */
  updateProfile: async (profileData: UpdateProfileData, serverToken?: string): Promise<User> => {
    const data = await apiClient.post<{ message: string; user_data: User } | ApiResponse<User>>(
      '/profile/update-profile',
      profileData,
      serverToken ? { serverToken } : undefined
    );
    if ('user_data' in data && data.user_data) {
      return data.user_data;
    }
    return (data as ApiResponse<User>).data;
  },

  /**
   * Update display picture (avatar)
   */
  updateDisplayPicture: async (file: File): Promise<User> => {
    const formData = new FormData();
    formData.append('dp', file);
    const data = await apiClient.post<ApiResponse<User>>('/profile/update-dp', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.data;
  },

  /**
   * Update email
   */
  updateEmail: async (emailData: UpdateEmailData): Promise<User> => {
    const data = await apiClient.post<ApiResponse<User>>('/profile/update-email', emailData);
    return data.data;
  },

  /**
   * Update contact info
   */
  updateContact: async (contactData: UpdateContactData): Promise<User> => {
    const data = await apiClient.post<ApiResponse<User>>('/profile/update-contact', contactData);
    return data.data;
  },

  /**
   * Update password
   */
  updatePassword: async (
    passwordData: UpdatePasswordData,
    serverToken?: string
  ): Promise<{ message: string }> => {
    return apiClient.post<{ message: string }>(
      '/profile/update-password',
      passwordData,
      serverToken ? { serverToken } : undefined
    );
  },

  /**
   * Persist user subtitle/caption language preference.
   * `null` = subtitles off. BCP-47 tag (e.g. "en", "pt-BR") = on.
   * BE validator caps at 5 today; FE clamps to 10 forward-compat with the
   * pending BE bump (see docs/backend-requests entry for max:10).
   * BE response is flat `{ message: "" }` — no User envelope — so we echo
   * the input back to the caller and let optimistic-update own FE state.
   */
  updateSubtitleLanguage: async (lang: string | null): Promise<string | null> => {
    const payload =
      lang === null
        ? { subtitle_language: null }
        : { subtitle_language: lang.slice(0, SUBTITLE_LANGUAGE_MAX_LEN) };
    await apiClient.post<{ message?: string }>('/profile/subtitle-language', payload);
    return lang;
  },

  /**
   * Sign creator contract. BE returns the new (or existing) signature date
   * plus an idempotency flag. Response is flat — no nested User envelope.
   */
  signCreatorContract: async (): Promise<SignCreatorContractResponse> => {
    return apiClient.post<SignCreatorContractResponse>('/profile/contract/sign', {});
  },

  /**
   * Set location sharing for the public globe heatmap (opt-in with coords, or
   * opt-out flag-only). Sanctum-authed — the `/api/[...path]` proxy attaches the
   * Bearer token. BE snaps coords to 2dp and returns a coord-less
   * `{ message, location: { share_location, updated_at } }` envelope, so we
   * return the `location` for the store patch.
   */
  shareLocation: async (data: ShareLocationData): Promise<UserLocation> => {
    const res = await apiClient.post<{ message: string; location: UserLocation }>(
      '/me/location',
      data
    );
    return res.location;
  },
};

export interface SignCreatorContractResponse {
  message: string;
  already_signed: boolean;
  creator_contract_date: string;
}
